import { useRef, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { Heart, MessageCircle, Send, Bookmark, Mic, BarChart3, Plus, Play, Users } from "lucide-react";

export const INDRIL_TABS = ["Reels", "Stories", "Feed", "Messaging", "Creator Tools", "Live Audio"];

const grad = ["from-[#1e3a8a] to-[#0ea5e9]", "from-[#7c2d12] to-[#d4af37]", "from-[#111827] to-[#2563eb]", "from-[#422006] to-[#f59e0b]"];
const Avatar = ({ i = 0, size = "w-9 h-9", ring }) => (
  <span className={`${size} shrink-0 rounded-full bg-gradient-to-br ${grad[i % 4]} ${ring ? "ring-2 ring-offset-2 ring-offset-black ring-[#d4af37]" : ""}`} />
);

const Reels = () => (
  <div className="absolute inset-0 bg-gradient-to-b from-[#0b1a3a] via-[#0a0f1f] to-black">
    <div className="absolute inset-0 flex items-center justify-center"><Play className="w-12 h-12 text-white/30" /></div>
    <div className="absolute right-3 bottom-24 flex flex-col gap-5 text-white items-center">
      <Heart className="w-6 h-6 fill-[#ef4444] text-[#ef4444]" /><MessageCircle className="w-6 h-6" /><Send className="w-6 h-6" /><Bookmark className="w-6 h-6" />
    </div>
    <div className="absolute left-4 bottom-8 right-14 space-y-2">
      <div className="flex items-center gap-2"><Avatar i={1} size="w-7 h-7" /><span className="h-2.5 w-24 rounded bg-white/70" /></div>
      <span className="block h-2 w-40 rounded bg-white/40" /><span className="block h-2 w-28 rounded bg-white/30" />
    </div>
    <div className="absolute bottom-3 inset-x-4 h-0.5 bg-white/15"><motion.div className="h-full bg-[#d4af37]" animate={{ width: ["0%", "100%"] }} transition={{ duration: 6, repeat: Infinity, ease: "linear" }} /></div>
  </div>
);

const Stories = () => (
  <div className="absolute inset-0 bg-black p-3 pt-10">
    <div className="flex gap-1 mb-3">{[0, 1, 2, 3].map((i) => <span key={i} className={`h-0.5 flex-1 rounded ${i < 2 ? "bg-white" : "bg-white/25"}`} />)}</div>
    <div className="flex items-center gap-2 mb-3"><Avatar i={2} size="w-7 h-7" /><span className="h-2 w-20 rounded bg-white/70" /></div>
    <div className="h-[72%] rounded-2xl bg-gradient-to-br from-[#d4af37]/70 via-[#7c2d12] to-[#0b1a3a] flex items-end p-4">
      <span className="font-display text-white text-lg">Today's moment</span>
    </div>
  </div>
);

const Feed = () => (
  <div className="absolute inset-0 bg-[#05060a] pt-10 px-3 space-y-3 overflow-hidden">
    <div className="flex gap-3 pb-2 border-b border-white/5">{[0, 1, 2, 3, 0].map((i, k) => <Avatar key={k} i={i} size="w-10 h-10" ring={k === 0} />)}</div>
    {[0, 1].map((i) => (
      <div key={i} className="rounded-xl bg-white/[0.03] p-2">
        <div className="flex items-center gap-2 mb-2"><Avatar i={i + 1} size="w-6 h-6" /><span className="h-2 w-16 rounded bg-white/60" /></div>
        <div className={`h-28 rounded-lg bg-gradient-to-br ${grad[i + 2]}`} />
        <div className="flex gap-3 mt-2 text-white/80"><Heart className="w-4 h-4" /><MessageCircle className="w-4 h-4" /><Send className="w-4 h-4" /></div>
      </div>
    ))}
  </div>
);

const Messaging = () => (
  <div className="absolute inset-0 bg-[#05060a] pt-10 px-3 flex flex-col">
    <div className="flex items-center gap-2 pb-3 border-b border-white/5"><Avatar i={3} size="w-8 h-8" /><span className="h-2.5 w-20 rounded bg-white/70" /></div>
    <div className="flex-1 space-y-2 py-3 text-[11px]">
      {[["l", "Hey! Saw your new reel"], ["r", "Thanks — more coming soon"], ["l", "Collab this weekend?"], ["r", "Let's do it"]].map(([s, t], i) => (
        <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.35 }}
          className={`max-w-[75%] px-3 py-2 rounded-2xl ${s === "r" ? "ml-auto bg-[#0066ff] text-white" : "bg-white/10 text-white"}`}>{t}</motion.div>
      ))}
    </div>
    <div className="mb-4 h-9 rounded-full bg-white/10 flex items-center px-3 text-white/50 text-[11px]">Message…</div>
  </div>
);

const Creator = () => (
  <div className="absolute inset-0 bg-[#05060a] pt-10 px-3">
    <p className="text-white text-xs font-semibold mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#d4af37]" /> Creator Studio</p>
    <div className="h-28 rounded-xl bg-white/[0.04] p-3 flex items-end gap-1.5">
      {[30, 45, 38, 60, 52, 75, 68, 90].map((h, i) => (
        <motion.span key={i} className="flex-1 rounded-t bg-gradient-to-t from-[#0066ff] to-[#00f0ff]" initial={{ height: 0 }} animate={{ height: `${h}%` }} transition={{ delay: i * 0.06, duration: 0.6 }} />
      ))}
    </div>
    <div className="grid grid-cols-2 gap-2 mt-3">
      {["Schedule", "Drafts", "Audience", "Insights"].map((t) => <div key={t} className="rounded-lg bg-white/[0.04] p-3 text-[11px] text-white/80">{t}</div>)}
    </div>
    <div className="mt-3 rounded-lg border border-dashed border-[#d4af37]/40 p-3 text-[11px] text-[#f3e5ab] flex items-center gap-2"><Plus className="w-4 h-4" /> New post</div>
  </div>
);

const Audio = () => (
  <div className="absolute inset-0 bg-gradient-to-b from-[#0a0f1f] to-black pt-10 px-4">
    <p className="text-white text-xs font-semibold flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> LIVE · Audio Room</p>
    <div className="grid grid-cols-3 gap-4 mt-6">
      {[0, 1, 2, 3, 0, 1].map((i, k) => (
        <div key={k} className="flex flex-col items-center gap-1">
          <span className="relative">
            {k < 2 ? <span className="absolute inset-0 rounded-full border-2 border-[#00f0ff] pulse-ring" /> : null}
            <Avatar i={i} size="w-12 h-12" />
          </span>
          <span className="h-1.5 w-10 rounded bg-white/40" />
        </div>
      ))}
    </div>
    <div className="absolute bottom-6 inset-x-4 flex items-center justify-between">
      <span className="flex items-center gap-1 text-white/70 text-[11px]"><Users className="w-4 h-4" /> Listening</span>
      <span className="w-11 h-11 rounded-full bg-[#d4af37] flex items-center justify-center"><Mic className="w-5 h-5 text-black" /></span>
    </div>
  </div>
);

const SCREENS = { Reels, Stories, Feed, Messaging, "Creator Tools": Creator, "Live Audio": Audio };

export const Phone = ({ tab = "Reels", className = "" }) => {
  const S = SCREENS[tab] || Reels;
  return (
    <div className={`relative w-[260px] h-[540px] rounded-[44px] p-[10px] bg-gradient-to-b from-[#3a3322] via-[#151515] to-[#2a2416] shadow-[0_40px_120px_-20px_rgba(0,102,255,0.45),0_0_0_1px_rgba(212,175,55,0.35)] ${className}`}>
      <div className="relative w-full h-full rounded-[36px] overflow-hidden bg-black">
        <motion.div key={tab} className="absolute inset-0" initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
          <S />
        </motion.div>
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 rounded-full bg-black z-10" />
      </div>
    </div>
  );
};

export const TiltPhone = ({ tab }) => {
  const ref = useRef(null);
  const rx = useSpring(useMotionValue(0), { stiffness: 120, damping: 14 });
  const ry = useSpring(useMotionValue(0), { stiffness: 120, damping: 14 });
  const move = (e) => {
    const r = ref.current.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 18);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 18);
  };
  return (
    <div ref={ref} onPointerMove={move} onPointerLeave={() => { rx.set(0); ry.set(0); }} style={{ perspective: 1200 }} className="flex justify-center py-6">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="float-y">
        <Phone tab={tab} />
      </motion.div>
    </div>
  );
};

export const PhoneShowcase = () => {
  const [tab, setTab] = useState("Reels");
  return (
    <div className="grid lg:grid-cols-[1fr_auto] gap-10 items-center" data-testid="indril-phone-showcase">
      <div className="flex lg:flex-col flex-wrap gap-2 order-2 lg:order-1" role="tablist" aria-label="INDRIL features">
        {INDRIL_TABS.map((t, i) => (
          <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)} data-testid={`indril-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
            className={`group flex items-center gap-4 text-left rounded-full lg:rounded-none lg:border-b px-4 lg:px-0 py-3 min-h-[44px] transition-colors ${tab === t ? "bg-[#d4af37] text-black lg:bg-transparent lg:text-[#f3d77a] lg:border-[#d4af37]" : "bg-white/5 text-slate-300 lg:bg-transparent lg:border-white/10 hover:text-white"}`}>
            <span className="hidden lg:inline font-mono text-xs opacity-70">{String(i + 1).padStart(2, "0")}</span>
            <span className="font-display text-base lg:text-3xl">{t}</span>
          </button>
        ))}
      </div>
      <div className="order-1 lg:order-2">
        <TiltPhone tab={tab} />
        <p className="text-center font-mono text-[11px] text-slate-400 tracking-wider">ILLUSTRATIVE INTERFACE PREVIEW</p>
      </div>
    </div>
  );
};
